export interface AppTokens {
  access: string;
  refresh: string;
  accessExpiry?: number; // Unix timestamp when access token expires
  refreshExpiry?: number; // Unix timestamp when refresh token expires
}

const ACCESS_TOKEN_KEY = "eledone_addin_accessToken";
const REFRESH_TOKEN_KEY = "eledone_addin_refreshToken";
const ACCESS_EXPIRY_KEY = "eledone_addin_accessExpiry";
const REFRESH_EXPIRY_KEY = "eledone_addin_refreshExpiry";

/**
 * Simple event emitter for token changes
 */
class TokenEventEmitter {
  private listeners: ((event: "tokens-set" | "tokens-cleared") => void)[] = [];

  subscribe(callback: (event: "tokens-set" | "tokens-cleared") => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback);
    };
  }

  emit(event: "tokens-set" | "tokens-cleared") {
    this.listeners.forEach((callback) => callback(event));
  }
}

const tokenEmitter = new TokenEventEmitter();

/**
 * Manages the storage and retrieval of the application's JWTs (access and refresh tokens).
 */
function setTokens(tokens: AppTokens): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
  if (tokens.accessExpiry) {
    localStorage.setItem(ACCESS_EXPIRY_KEY, tokens.accessExpiry.toString());
  }
  if (tokens.refreshExpiry) {
    localStorage.setItem(REFRESH_EXPIRY_KEY, tokens.refreshExpiry.toString());
  }
  tokenEmitter.emit("tokens-set");
}

function getTokens(): AppTokens | null {
  const access = localStorage.getItem(ACCESS_TOKEN_KEY);
  const refresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  const accessExpiry = localStorage.getItem(ACCESS_EXPIRY_KEY);
  const refreshExpiry = localStorage.getItem(REFRESH_EXPIRY_KEY);

  if (access && refresh) {
    return {
      access,
      refresh,
      accessExpiry: accessExpiry ? parseInt(accessExpiry) : undefined,
      refreshExpiry: refreshExpiry ? parseInt(refreshExpiry) : undefined,
    };
  }
  return null;
}

function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ACCESS_EXPIRY_KEY);
  localStorage.removeItem(REFRESH_EXPIRY_KEY);
  tokenEmitter.emit("tokens-cleared");
}

/**
 * Check if access token is expired or will expire soon (within 5 minutes)
 */
function isAccessTokenExpired(bufferMs: number = 5 * 60 * 1000): boolean {
  const tokens = getTokens();
  if (!tokens?.accessExpiry) return false;
  return Date.now() + bufferMs >= tokens.accessExpiry;
}

/**
 * Check if refresh token is expired
 */
function isRefreshTokenExpired(): boolean {
  const tokens = getTokens();
  if (!tokens?.refreshExpiry) return false;
  return Date.now() >= tokens.refreshExpiry;
}

export const sessionService = {
  setTokens,
  getTokens,
  clearTokens,
  isAccessTokenExpired,
  isRefreshTokenExpired,
  onTokenChange: tokenEmitter.subscribe.bind(tokenEmitter),
};
